import { useState } from "react";

import { useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

import { createAdminUser } from "../../utils/client";
import { containsUpperCase, isAdmin } from "../../utils/helper";
import {
  Box,
  CircularProgress,
  Container,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useAlert } from "../../components/AlertProvider";
import { ValidationError } from "baptize-america-client";
import { LoginWindow } from "../../components/LoginWindow";
import { useNavigate } from "react-router-dom";
import DebouncedButton from "../../components/DebounceButton";
import TopNavBar from "../../components/TopNavBar";
import { useUserData } from "../../components/DataProvider";

const styles = {
  navBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    mb: 5,
  },
  innerBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    mt: 2,
    mb: 2,
    pt: 2,
    pb: 2,
    border: 1,
    borderColor: "#D6E4FF",
    borderRadius: 2,
    width: "70vw",
  },
  actionButton: {
    width: 300,
    height: 60,
    backgroundColor: "#00169E",
    color: "#FFF",
    fontSize: 16,
  },
};

const CreateAdminPage = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { authStatus } = useAuthenticator((context) => [context.user]);
  const { attributes } = useUserData();
  const { setAlert } = useAlert();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [confirmEmail, setConfirmEmail] = useState<string>("");

  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    confirmEmail?: string;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);

  const validateInputs = () => {
    const newErrors: typeof errors = {};

    if (!firstName.trim()) newErrors.firstName = "First name is required.";
    if (!lastName.trim()) newErrors.lastName = "Last name is required.";
    if (!email.trim()) newErrors.email = "Email is required.";
    if (containsUpperCase(email))
      newErrors.email = "Email must not contain uppercase letters.";
    if (email !== confirmEmail) {
      newErrors.confirmEmail = "Emails do not match.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleCreateAdmin = async () => {
    if (!isAdmin(attributes)) {
      setAlert({
        showAlert: true,
        alertMsg: "Only Admins can create admins.",
        alertType: "error",
      });
      return;
    }

    if (!validateInputs()) return;

    try {
      setLoading(true);
      await createAdminUser({ firstName, lastName, email });
      setLoading(false);
      setAlert({
        showAlert: true,
        alertMsg: `An email with their temporary password has been sent to: ${email}`,
        alertType: "success",
      });
      navigate("/admin", { replace: true });
    } catch (err) {
      setLoading(false);
      console.log(`* handleCreateAdmin - error obj: ${JSON.stringify(err)}`);
      setAlert({
        showAlert: true,
        alertMsg:
          (err as unknown as ValidationError).errorMessage ??
          "Error Creating Admin User",
        alertType: "error",
      });
    }
  };

  return authStatus === "authenticated" && isAdmin(attributes) ? (
    <div>
      <Container maxWidth={isLargeScreen ? "md" : false}>
        <Box
          sx={[
            {
              "& .MuiTextField-root": {
                m: 1,
                width: theme.breakpoints.values.sm,
              },
            },
            styles.navBar,
          ]}
          component="form"
          noValidate
          autoComplete="off"
        >
          <Box sx={{ mb: 4 }}>
            <TopNavBar showBackArrow={true} />
          </Box>
          <div>
            <TextField
              required
              id="outlined-required"
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
          </div>
          <div>
            <TextField
              required
              id="outlined-required"
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
          </div>
          <div>
            <TextField
              required
              id="outlined-required"
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
            />
          </div>
          <div>
            <TextField
              required
              id="outlined-required"
              label="Confirm Email Address"
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              error={!!errors.confirmEmail}
              helperText={errors.confirmEmail}
            />
          </div>
          <Stack direction="column" sx={{ mt: 4 }}>
            <DebouncedButton
              onClick={handleCreateAdmin}
              variant="contained"
              style={styles.actionButton}
              disabled={loading}
              endIcon={
                loading ? <CircularProgress size={24} color="inherit" /> : null
              }
              debounceTime={3000}
            >
              {loading ? "" : "Create Admin User"}
            </DebouncedButton>
          </Stack>
        </Box>
      </Container>
    </div>
  ) : (
    <LoginWindow />
  );
};

export default CreateAdminPage;
